import { UseFormReturn } from "react-hook-form";
import MarkdownPreview from "@uiw/react-markdown-preview";
import { Suspense } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Spinner } from "@/components/ui/spinner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { clientOnboardingType } from "../../validator";
import useCurrentUserStore from "@/store";
import { cn, getAvatarFallback } from "@/lib/utils";

type PropTypes = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<clientOnboardingType, any, undefined>;
};

const Step4ContentPreview: React.FC<PropTypes> = (props) => {
  const step4Data = props.form.watch("step4");
  const appName = props.form.watch("step2.slug");
  const { blogImage } = useCurrentUserStore();
  return (
    <section
      className="p-4 lg:h-[99vh]"
      style={{
        color: step4Data.primaryColor,
        backgroundColor: step4Data.backgroundColor,
      }}
    >
      {/* AI Lab Logo */}
      <img src="/logo.svg" alt="ai-lab-logo" className="block lg:hidden mb-6" />
      {/* Logo */}
      <Avatar>
        <AvatarImage src={step4Data.logo} alt="logo" />
        <AvatarFallback className={`uppercase font-semibold text-black`}>
          {getAvatarFallback(appName)}
        </AvatarFallback>
      </Avatar>

      <div className="flex  flex-col items-center gap-8">
        {/* Header */}
        <Suspense
          fallback={
            <div className="flex items-center justify-center">
              <Spinner />
            </div>
          }
        >
          <SuspenseTitlePreview form={props.form} />
        </Suspense>
        <ScrollArea
          className="h-[75vh] p-3 text-current"
          scrollBarClassName="bg-gray-200"
          scrollThumbClassName="bg-gray-300"
        >
          <div className="flex justify-center mb-8">
            <img src={blogImage} className="h-80 w-[620px] " />
          </div>
          <Suspense
            fallback={
              <div className="flex items-center justify-center">
                <Spinner />
              </div>
            }
          >
            <SuspenseMarkDownPreview form={props.form} />
          </Suspense>
        </ScrollArea>
      </div>
    </section>
  );
};

export default Step4ContentPreview;

// ####################### INTERNAL COMPONENTS #######################

/**
 *
 * @param props form: UseFormReturn<clientOnboardingType, any, undefined>;
 * @description Suspense component for Markdown preview
 * @returns JSX.Element
 */
const SuspenseMarkDownPreview: React.FC<PropTypes> = (props) => {
  const theme = props.form.watch("step4");
  // const blogContent = props.form.watch("step3.blogContent");
  const { generatedBlog } = useCurrentUserStore();

  const fontUrl = `https://fonts.googleapis.com/css2?family=${theme.bodyFont}&display=swap`;

  const res = useSuspenseQuery({
    queryKey: [fontUrl],
    queryFn: () => fetch(fontUrl).then((res) => res.text()),
  });

  return (
    <>
      {/* font family being set */}
      <style>{res.data}</style>

      {/* Content Mark down preview */}

      <MarkdownPreview
        source={JSON.parse(generatedBlog)?.blog_content}
        style={{
          width: "100%",
          fontFamily: theme.bodyFont,
          color: theme.appearance === "light" ? "#000000" : "#ffffff",
          backgroundColor: theme.backgroundColor,
        }}
        rehypeRewrite={(node) => {
          if (
            node.type === "element" &&
            node.tagName !== "svg" &&
            node.tagName !== "a"
          ) {
            // check if child element is
            node.properties = {
              style: `color: ${
                theme.appearance === "light" ? "#000000" : "#ffffff"
              }`,
            };
          }
        }}
      />
    </>
  );
};

/**
 *
 * @param props form: UseFormReturn<clientOnboardingType, any, undefined>;
 * @description Suspense component for title preview
 * @returns JSX.Element
 */
const SuspenseTitlePreview: React.FC<PropTypes> = (props) => {
  const headerFont = props.form.watch("step4.headerFont");

  const fontUrl = `https://fonts.googleapis.com/css2?family=${
    headerFont || "Inter"
  }&display=swap`;

  const res = useSuspenseQuery({
    queryKey: [fontUrl],
    queryFn: () => fetch(fontUrl).then((res) => res.text()),
  });
  const theme = props.form.watch("step4");

  return (
    <>
      <style>{res.data}</style>
      <h1
        className={`text-4xl font-bold text-[${
          props.form.watch("step4")?.primaryColor
        }]`}
        style={{
          fontFamily: headerFont || "Inter",
          color: `${props.form.watch("step4")?.primaryColor}`,
        }}
      >
        {props.form.watch("step2.appName")}
      </h1>
      <p className={cn(theme?.appearance === "light" ? "text-md text-black" : "text-md text-white")}>{props.form.watch("step2.description")}</p>
    </>
  );
};
